import { toLuxon } from "../../shared/dateTimeUtils";

/**
 * Utility function for returning unique segments by timestamp in ascending order
 * @export
 * @param {Object[]} segments array of segments
 * @return {Object[]} unique segments by timestamp in ascending order
 */
export function getUniqueOrderedSegments(segments) {
  if (!segments.length) return [];
  // get unique data by timestamp and largest number of waveData objects
  const segmentsMap = segments.reduce((map, segment) => {
    const timestamp = Number(segment.dateTime || new Date(segment.timestamp));
    const storedSegment = map.get(timestamp);
    if (storedSegment?.waveData?.length && !segment.waveData?.length)
      return map;
    map.set(timestamp, segment);
    return map;
  }, new Map());
  // sort by timestamp
  return [...segmentsMap]
    .sort(([aTime], [bTime]) => aTime - bTime)
    .map((entry) => entry[1]);
}

export function getMinMarkerDeltaByPosition(m1, m2) {
  return Math.min(m1, m2);
}

export function getMaxMarkerDeltaByPosition(m1, m2) {
  return Math.max(m1, m2);
}

export function getMarkerDeltaByTimestamp(
  stripWidth,
  m1,
  m2,
  chartEndTime,
  pixelsPerSecond,
  isLeft,
) {
  const M1 = toLuxon(m1);
  const M2 = toLuxon(m2);
  const CHART_END = toLuxon(chartEndTime);
  let diff;

  if (isLeft) {
    if (M1 < M2) {
      diff = M1.diff(CHART_END, "seconds").as("seconds");
    } else {
      diff = M2.diff(CHART_END, "seconds").as("seconds");
    }
  } else {
    if (M1 > M2) {
      diff = M1.diff(CHART_END, "seconds").as("seconds");
    } else {
      diff = M2.diff(CHART_END, "seconds").as("seconds");
    }
  }

  return [stripWidth, diff, pixelsPerSecond].reduce(
    (sum, value) => sum + value,
  );
}
export function getLeftMarkerDeltaByTimestamp(
  stripWidth,
  m1,
  m2,
  chartEndTime,
  pixelsPerSecond,
) {
  return getMarkerDeltaByTimestamp(
    stripWidth,
    m1,
    m2,
    chartEndTime,
    pixelsPerSecond,
    true,
  );
}

export function getRightMarkerDeltaByTimestamp(
  stripWidth,
  m1,
  m2,
  chartEndTime,
  pixelsPerSecond,
) {
  return getMarkerDeltaByTimestamp(
    stripWidth,
    m1,
    m2,
    chartEndTime,
    pixelsPerSecond,
    false,
  );
}

export function getMarkerDeltaByCanvasWidth(canvasWidth, m1, m2) {
  return canvasWidth - getMaxMarkerDeltaByPosition(m1, m2);
}
/**
 *
 * @param {object} options
 * @param {object} options.group a waveGroup from PM config, typically waveGroups[0]
 * @param {number} options.pixelsPerSecond defines scale
 * @param {number} options.yOffset negative DC offset in pixels, ie the distance down from the top of the canvas
 * @param {number} options.chartHeight The height to scale the wave to, given its group's range properties, used for waves without mvPerPoint
 * @returns {function} a scaler function
 *  @param {number} value input value for a point on the wave.
 *  @returns {number} the Y coordinate on the canvas.
 */
export function generateYScale({ group, pixelsPerSecond, yOffset }) {
  const pointRange = group.maxRange - group.minRange;
  const defaultMvPerPoint = rangeMv / pointRange;
  const mvPerPoint = group.mvPerPoint || defaultMvPerPoint;
  const pixelsPerMv = pixelsPerSecondToPixelsPerMv(pixelsPerSecond);
  const pixelsPerPoint = pixelsPerMv * mvPerPoint;
  const yIntercept = group.minRange * pixelsPerPoint + yOffset;
  const slope = -pixelsPerPoint;
  const yScale = (value) => {
    return value * slope + yIntercept;
  };
  yScale.update = ({ min, max } = {}) => {
    if ([min, max].some((n) => n === null || isNaN(n))) return yScale;
    if (
      Number(max) === Number(group.maxRange) &&
      Number(min) === Number(group.minRange)
    )
      return yScale;
    return generateYScale({
      group: { ...group, minRange: min, maxRange: max },
      pixelsPerSecond,
      yOffset,
    });
  };
  return yScale;
}

// Default mvPerPoint should be 0.001 at a range of 1600 (+800 to -800).
// Therefore or the default range should be 1.6mv.
const rangeMv = 1.6;

// Ratio of height / seconds. (It's a constant 0.64).
export const chartHeightPerSecond = pixelsPerSecondToPixelsPerMv(rangeMv);

export function getChartHeight({ pixelsPerSecond }) {
  return chartHeightPerSecond * pixelsPerSecond;
}

/**
 * https://airstrip.atlassian.net/browse/AS1-4261
 * 1. 1 major lines should equal 0.2 seconds (5 per second)
 * 2. 1 minor line should equal 0.04 seconds (5 per major grid line or 25 per second)
 * https://airstrip.atlassian.net/browse/AS1-4285
 * Major grid lines for every 0.5mv
 * Minor grid line for every 0.1mv
 * The grid should be square, therefore the distance in pixels for 0.5mv = 0.2 seconds, or 1mv per 0.4 seconds.
 * @export
 * @param {number} pixelsPerSecond
 * @return {number} pixels per millivolt
 */
export function pixelsPerSecondToPixelsPerMv(pixelsPerSecond) {
  return pixelsPerSecond * 0.4;
}
/**
 * @export
 * @param {array} measurements Array of current measurement data in MonitorStore
 * @return {array} Array of alarms to display on monitor
 */
export function getAlarms(measurementData) {
  if (!measurementData) return [];
  const levels = new Map();
  ["High", "Medium", "Low"].forEach((level) => levels.set(level, new Map()));

  const alarmsByLevel = measurementData
    .reverse()
    .reduce((_levels, segment) => {
      const alarms = segment.eventData || [];
      alarms
        .map((_alarm) => {
          return {
            ..._alarm,
            timestamp: segment.timestamp,
          };
        })
        .forEach((alarm) => {
          return _levels.get(alarm.level)?.set(alarm.message, alarm);
        });
      return _levels;
    }, levels)
    .values();
  const alarms = Array.from(alarmsByLevel).flatMap((l) =>
    Array.from(l.values()),
  );

  return alarms;
}

export function getHighestAlarm(measurementData) {
  if (!measurementData) return [];
  const levels = { High: 3, Medium: 2, Low: 1, None: 0 };
  let highestLevel = -1;
  let highestAlarm = null;
  measurementData.reverse().some((segment) => {
    const alarms = segment.eventData;
    if (!alarms) return false;
    return alarms.some((alarm) => {
      const levelIndex = levels[alarm.level];
      if (levelIndex > highestLevel) {
        highestLevel = levelIndex;
        highestAlarm = alarm;
        if (levelIndex === 3) return true;
      }
    });
  });
  return highestAlarm;
}
